import { getHostAdapter } from "@cinesys/experience-ui/host";

/** Shape-compatible with Cantemo's real `shared/ApiService`'s `FetchError` (`isFetchError`,
 *  `status`, `.message`) — `fileActions.ts` duck-types on `isFetchError`. The shared `HostAdapter`'s
 *  `api.fetch` (`customerAppsHostAdapter.ts`) is what throws this now (Monday S2.5a) — it's the
 *  one place that still sees `CustomerApiService`'s own `ApiError` to translate from. Kept here,
 *  not there, because this is the shape callers in this folder duck-type on. */
export class FetchError extends Error {
  public isFetchError = true;
  public status: number;
  public response: Record<string, string | number> | undefined;
  constructor(status: number, response?: Record<string, string>) {
    super(response?.message || "Unknown error");
    this.status = status;
    this.response = response;
  }
}

/**
 * Stand-in for Cantemo's `shared/ApiService` import — the one permitted adaptation per
 * `docs/implementation-plan.md` §0 R2 ("the only permitted edit is feature flags... The 'shared'
 * interface is just cantemo's exactly"). Every ported call site keeps its literal Cantemo path
 * string (`/cs_api/cs_item_browser/...`) unedited.
 *
 * Forwards to the shared `HostAdapter`'s `api.fetch` now (Monday S2.5a) instead of importing the
 * concrete `CustomerApiService` directly — same seam `../../../components/CollectionBrowser/
 * platform.ts` and `../../../components/MetadataViewer/platform.ts` already route their own
 * `ApiService`-shaped forwarding through. This file keeps its old default-export class shape
 * (rather than switching to those two files' named-const-object shape) deliberately: every call
 * site in this folder still imports `ApiService` as a default export, and this hasn't moved into
 * the package yet, so there's no reason to churn ~20 call sites' import lines for a cosmetic
 * shape match. `customerAppsHostAdapter.ts`'s `api.fetch` is what now does the `/cs_api` prefix
 * strip this file used to do directly.
 */
export default class ApiService {
  static fetch<T, B = unknown>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body?: B
  ): Promise<T> {
    return getHostAdapter().api.fetch<T, B>(method, path, body);
  }

  /** Cantemo's version returns the raw `Response` instead of parsed JSON, for a couple of call
   *  sites that don't need the body. `CustomerApiService` only exposes the parsed-JSON `fetch`
   *  above; every reachable caller here (`RemoveMetadataModal.tsx`) discards the return value
   *  entirely, so delegating straight to `fetch` is behavior-equivalent for them. */
  static fetchRaw<T, B = unknown>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body?: B
  ): Promise<T> {
    return ApiService.fetch<T, B>(method, path, body);
  }
}
